import { Box } from "@chakra-ui/react";
import React, {
  useCallback,
  useMemo,
  useState,
  useEffect
} from "react";
import { useHistory } from "react-router-dom";
import { Column } from "react-table";
import {
  format,
  formatDuration
} from "date-fns";

import { useConversationsApi } from "src/api/conversations/conversations.api";
import { Header } from "src/components/shared/Header";
import { Text } from "src/components/text";
import { Conversation } from "src/api";
import { Table } from "src/components/table";
import { useAuthContext } from "src/hooks/useAuthContext";
import { Avatar } from "src/components/avatar";

interface ConversationsTableData {
  _id: Conversation["_id"]
  date: Conversation["date"]
  startTime: Conversation["startTime"]
  endTime: Conversation["endTime"]
  latencySeconds: Conversation["latencySeconds"]
  mode: Conversation["mode"]
  creator?: string
  partner?: string
  accepted?: string
}
export const ConversationInvitationsPage: React.FC = () => {
  const history = useHistory();
  const [ conversationsRes, conversationsReq ] = useConversationsApi("GET_CONVERSATIONS");
  const [ conversationsTableData, setConversationsTableData ] = useState<ConversationsTableData[]>([]);
  const { authDataState } = useAuthContext();

  const columns = useMemo(
    (): Array<Column<ConversationsTableData>> => [
      {
        Header: "Mode",
        accessor: "mode",
        Cell: props => (
          <div>
            {props.value === "voice" ? "Voice LGV" : "Message LGM"}
          </div>
        )
      },
      {
        Header: "Date",
        accessor: "date",
        Cell: props => (
          <div>
            {format(new Date(props.value), "yyyy/MM/dd")}
          </div>
        )

      },
      {
        Header: "Start Time",
        accessor: "startTime",
        Cell: props => (
          <div>
            {format(new Date(props.value), "HH:mm")}
          </div>
        )
      },
      {
        Header: "End Time",
        accessor: "endTime",
        Cell: props => (
          <div>
            {format(new Date(props.value), "HH:mm")}
          </div>
        )
      },
      {
        Header: "Latency",
        accessor: "latencySeconds",
        Cell: props => (
          <div>
            {props.value === 0 ? "No latency" : formatDuration({
              minutes: Math.floor(props.value / 60),
              seconds: props.value - Math.floor(props.value / 60) * 60
            })}
          </div>
        )
      },
      {
        Header: "Partner",
        accessor: "partner",
        Cell: props => (
          <Avatar
            name={props.value || ""}
            size="sm"
          />
        )
      },
      {
        Header: "Accepted",
        accessor: "accepted"
      }
    ],
    []
  );

  const handleFetch = useCallback((pageIndex: number, pageSize: number) => {
    const skip = pageIndex * pageSize;

    conversationsReq({
      skip,
      filter: { endTime: { $gt: new Date() } },
      sort: { "fullConversation.startTime": 1 },
      limit: pageSize
    });
  }, [ conversationsReq ]);

  useEffect(() => {
    if (conversationsRes.data) {
      const conversationsData = conversationsRes.data.results.map(conversation => {
        return {
          ...conversation,
          partner: conversation.participants?.find(p => p.user._id !== authDataState.user?._id)?.user.name,
          accepted: conversation.participants?.find(p => !p.creator)?.accepted ? "Yes" : "No"
        };
      });

      setConversationsTableData(conversationsData);
    }
  }, [ conversationsRes, authDataState ]);

  const pageCount = useMemo(() =>
    conversationsRes.data ?
      conversationsRes.data?.total < 10 ? 1 : Math.ceil(conversationsRes.data?.total / 10) :
      1,
  [ conversationsRes ]);

  return (
    <Box p={10}>
      <Header />

      <Box paddingY={10}>
        <Text as="h1">
          Invitations
        </Text>

        <Box paddingY={4}>
          <Text as="p">
            Manage your upcoming sessions
          </Text>
        </Box>

        <Table
          columns={columns}
          fetchData={handleFetch}
          loading={conversationsRes.pending}
          pageCount={pageCount}
          handleClick={conversation => history.push(`/conversation/${conversation.original._id}`)}
          data={conversationsTableData || []}
        />

      </Box>

    </Box>
  );
};
